import {css, StyleSheet} from 'aphrodite';
import {produce} from 'immer';
import React from 'react';

import {OligoAwareFolderItemType} from 'constants/RegistryConstants';
import SaveLocationPicker, {
  DEFAULT_WORKLIST_PARAMS,
  LocationInformation,
  WorklistParams,
} from 'molbio/components/SaveLocationPicker';
import {WorklistFormType} from 'worklists/WorklistConstants';

interface PickLocationStepRequiredParams {
  isRequired: true;
}

interface PickLocationStepNotRequiredParams {
  isRequired: false;
  checkboxLabel: string;
  checkboxTooltip: string;
  saveEntities: boolean;
  onUpdateSaveEntities: (saveEntities: boolean) => void;
  bannerText: string;
}

interface SaveConstructsStepProps {
  locationInfo: LocationInformation;
  onUpdateLocationInfo: (locationInfo: LocationInformation) => void;
  worklistParams: WorklistParams;
  stepDescription?: string;
  isRequiredParams: PickLocationStepRequiredParams | PickLocationStepNotRequiredParams;
  oligoAwareFolderItemTypes: Array<OligoAwareFolderItemType> | null;
}

export function SaveConstructsStep(props: SaveConstructsStepProps): JSX.Element {
  return (
    <>
      {props.stepDescription && <p className={css(styles.description)}>{props.stepDescription}</p>}
      <SaveLocationPicker
        targetFolderId={props.locationInfo.targetFolderId}
        onTargetFolderIdChange={(e) => {
          props.onUpdateLocationInfo({...props.locationInfo, targetFolderId: e.target.value});
        }}
        tagSchemaInfo={
          props.oligoAwareFolderItemTypes != null
            ? {
                tagSchemaId: props.locationInfo.tagSchemaId,
                onUpdateTagSchemaId: (tagSchemaId) => {
                  props.onUpdateLocationInfo({...props.locationInfo, tagSchemaId});
                },
                oligoAwareFolderItemTypes: props.oligoAwareFolderItemTypes,
              }
            : null
        }
        worklistFormType={props.locationInfo.worklistParams?.worklistFormType ?? WorklistFormType.NEW}
        onWorklistFormTypeChange={(newOrExisting) => {
          if (newOrExisting === WorklistFormType.NEW) {
            props.onUpdateLocationInfo(
              produce(props.locationInfo, (draft) => {
                draft.worklistParams!.targetWorklistId = null;
                draft.worklistParams!.worklistFormType = WorklistFormType.NEW;
              })
            );
          } else {
            props.onUpdateLocationInfo(
              produce(props.locationInfo, (draft) => {
                draft.worklistParams!.newWorklistInfo = null;
                draft.worklistParams!.worklistFormType = WorklistFormType.EXISTING;
              })
            );
          }
        }}
        newWorklistName={props.locationInfo.worklistParams?.newWorklistInfo?.name ?? ''}
        onNewWorklistNameChange={(e) => {
          props.onUpdateLocationInfo(
            produce(props.locationInfo, (draft) => {
              if (e.target.value) {
                draft.worklistParams!.newWorklistInfo = {name: e.target.value};
              } else {
                draft.worklistParams!.newWorklistInfo = null;
              }
            })
          );
        }}
        onWorklistIdChange={(targetWorklist) => {
          props.onUpdateLocationInfo(
            produce(props.locationInfo, (draft) => {
              draft.worklistParams!.targetWorklistId = targetWorklist == null ? null : targetWorklist.id;
            })
          );
        }}
        worklistParams={props.worklistParams}
        required={true}
        useWorklist={props.locationInfo.worklistParams != null}
        uniqueId="redesign"
        onUseWorklistChange={(useWorklist: boolean) => {
          props.onUpdateLocationInfo(
            produce(props.locationInfo, (draft) => {
              draft.worklistParams = useWorklist ? DEFAULT_WORKLIST_PARAMS : null;
            })
          );
        }}
      />
    </>
  );
}

const styles = StyleSheet.create({
  description: {marginTop: 10, marginBottom: 10},
});
